import React, { useEffect, useState } from "react";
import { apiGet, formatNumber } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, BarChart3 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

/**
 * EpsHistoryChart — Phase 7 Stage 2
 *
 * Small bar chart of annual EPS over the years extracted from CSE annual
 * reports. Used on the Stock Detail Fundamentals tab to visualise lumpy
 * earnings (latest bar towers over the median).
 *
 * Props:
 *   - companyCode (string)         CSE company code (e.g. "JKH")
 *   - lumpyDetected (bool)         from composite_scores.layer2_lumpy_earnings
 *   - medianEps (number|null)      median EPS used for the lumpy detector
 *   - latestEps (number|null)      latest EPS
 *   - spikeRatio (number|null)     latest / median
 */
const EpsHistoryChart = ({
  companyCode,
  lumpyDetected = false,
  medianEps = null,
  latestEps = null,
  spikeRatio = null,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyCode) return;
    setLoading(true);
    apiGet(`/historical-fundamentals/${companyCode}?period_type=annual`)
      .then((data) => {
        const cleaned = (data || [])
          .filter((d) => d.eps != null)
          .map((d) => ({
            year: String(d.financial_year || ""),
            eps: Number(d.eps),
          }))
          .sort((a, b) => a.year.localeCompare(b.year));
        setRows(cleaned);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [companyCode]);

  const hasData = rows.length >= 1;
  const latestYearIdx = rows.length - 1;

  return (
    <Card className="border bg-card" data-testid="eps-history-chart">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              EPS history (annual)
            </CardTitle>
            <CardDescription>
              Multi-year earnings extracted from CSE annual reports. The
              dashed line marks the historical median.
            </CardDescription>
          </div>
          {lumpyDetected && (
            <Badge
              variant="outline"
              className="text-[11px] gap-1.5 bg-warning/10 text-warning border-warning/40"
              data-testid="lumpy-eps-chip"
            >
              <AlertTriangle className="h-3 w-3" />
              Lumpy EPS
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">
            Loading…
          </div>
        )}
        {!loading && !hasData && (
          <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">
            No multi-year EPS data extracted yet. The admin can run "Re-analyse Annual Reports Only" from Report Monitor.
          </div>
        )}
        {!loading && hasData && (
          <>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <XAxis
                    dataKey="year"
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatNumber(v, 1)}
                    width={45}
                  />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--muted) / 0.3)" }}
                    contentStyle={{
                      borderRadius: 6,
                      border: "1px solid hsl(var(--border))",
                      background: "hsl(var(--card))",
                      fontSize: 12,
                    }}
                    formatter={(v) => [formatNumber(v, 2), "EPS"]}
                    labelFormatter={(l) => `FY ${l}`}
                  />
                  {medianEps != null && (
                    <ReferenceLine
                      y={medianEps}
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="4 4"
                      label={{
                        value: `median ${formatNumber(medianEps, 2)}`,
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 10,
                        position: "right",
                      }}
                    />
                  )}
                  <Bar dataKey="eps" radius={[4, 4, 0, 0]}>
                    {rows.map((_, i) => (
                      <Cell
                        key={i}
                        fill={
                          lumpyDetected && i === latestYearIdx
                            ? "hsl(var(--warning))"
                            : "hsl(var(--primary))"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              <span>
                Years on file: <span className="font-mono text-foreground">{rows.length}</span>
              </span>
              {latestEps != null && (
                <span>
                  Latest EPS: <span className="font-mono text-foreground">{formatNumber(latestEps, 2)}</span>
                </span>
              )}
              {medianEps != null && (
                <span>
                  Median EPS: <span className="font-mono text-foreground">{formatNumber(medianEps, 2)}</span>
                </span>
              )}
              {spikeRatio != null && (
                <span>
                  Latest/median:{" "}
                  <span className={`font-mono ${lumpyDetected ? "text-warning" : "text-foreground"}`}>
                    {formatNumber(spikeRatio, 2)}×
                  </span>
                </span>
              )}
            </div>
            {lumpyDetected && (
              <p className="mt-2 text-[11px] text-warning leading-snug" data-testid="lumpy-explanation">
                Latest EPS is {spikeRatio != null ? `${spikeRatio.toFixed(2)}× ` : ""}
                the historical median — likely a one-off windfall (asset sale,
                tax credit). Layer 2 EPS-growth score has been capped using
                median growth, not the spike year.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EpsHistoryChart;
